import { PlanType } from "@brookbush/database";
import { CheckoutStatus } from "@brookbush/types";

import { mutate as globalMutate } from "swr";
import { create } from "zustand";

import useAccess from "./useAccess";
import usePlanConfig from "./usePlanConfig";
import useStripePaymentMethods from "./useStripePaymentMethods";
import useStripeSubscription from "./useStripeSubscription";
import type { BecomeAMemberProvider } from "~/lib/becomeAMemberTracking";
import captureBecomeAMemberEvent from "~/lib/captureBecomeAMemberEvent";

interface CheckoutState {
	checkoutStatus: CheckoutStatus;
	paymentProvider: BecomeAMemberProvider | null;
	isPaymentSubmitting: boolean;
	startCheckout: () => void;
	completeCheckout: (provider: BecomeAMemberProvider) => void;
	clearCheckout: () => void;
	setPaymentSubmitting: (isSubmitting: boolean) => void;
}

const useCheckoutStore = create<CheckoutState>((set) => ({
	checkoutStatus: CheckoutStatus.IDLE,
	paymentProvider: null,
	isPaymentSubmitting: false,
	startCheckout: () =>
		set({
			checkoutStatus: CheckoutStatus.IN_PROGRESS,
			paymentProvider: null,
			isPaymentSubmitting: false,
		}),
	completeCheckout: (provider) => {
		set({
			checkoutStatus: CheckoutStatus.SUCCESSFUL,
			paymentProvider: provider,
			isPaymentSubmitting: false,
		});

		globalMutate("/onboarding/checklist");
	},
	clearCheckout: () =>
		set({
			checkoutStatus: CheckoutStatus.IDLE,
			paymentProvider: null,
			isPaymentSubmitting: false,
		}),
	setPaymentSubmitting: (isSubmitting) =>
		set({ isPaymentSubmitting: isSubmitting }),
}));

const useCheckout = () => {
	const {
		checkoutStatus,
		paymentProvider,
		isPaymentSubmitting,
		startCheckout,
		completeCheckout,
		clearCheckout,
		setPaymentSubmitting,
	} = useCheckoutStore();

	const { isLoadingPlanConfig, planConfig } = usePlanConfig();
	const { isLoadingPaymentMethods } = useStripePaymentMethods();
	const stripeSubscription = useStripeSubscription();

	const { isLoadingAccess, isMember } = useAccess();

	const isLoadingCheckout =
		isLoadingPlanConfig ||
		isLoadingPaymentMethods ||
		stripeSubscription.isLoading ||
		isLoadingAccess;

	const isChangingPlan =
		!isLoadingCheckout &&
		stripeSubscription.isActive &&
		stripeSubscription.planType === planConfig.planType &&
		(stripeSubscription.price!.id !== planConfig.priceId ||
			planConfig.planType === PlanType.INDIVIDUAL ||
			stripeSubscription.quantity !== planConfig.quantity);

	const isEligibleForYearlyUpgrade =
		!isLoadingCheckout &&
		stripeSubscription.isActive &&
		stripeSubscription.planType === PlanType.INDIVIDUAL &&
		stripeSubscription.price?.recurring!.interval === "month";

	const isExpressCheckoutEligible = !isLoadingAccess && !isMember;

	const startCheckoutWithTracking = () => {
		captureBecomeAMemberEvent("checkout_started", {
			surface: "page",
			interval: planConfig.price?.recurring?.interval,
			promo_code: planConfig.promoCode?.code,
			price_id: planConfig.priceId,
		});
		startCheckout();
	};

	return {
		checkoutStatus,
		paymentProvider,
		isPaymentSubmitting,

		startCheckout: startCheckoutWithTracking,
		completeCheckout,
		clearCheckout,
		setPaymentSubmitting,
		isLoadingCheckout,
		flags: {
			isChangingPlan,
			isEligibleForYearlyUpgrade,
			isExpressCheckoutEligible,
		},
	};
};

export default useCheckout;
